import { useState, useCallback, useEffect } from "react";
import { useWorkoutTemplates } from "./useWorkoutTemplates";
import { usePrescriptions } from "./usePrescriptions";
import { useAiWorkout } from "./useAiWorkout";
import { usePlan } from "../../../../hooks/usePlan";
import { FEATURE_KEYS } from "../../../../constants/plans";
import { ConfirmDialog } from "../../../../components/Common";
import { initialTemplateValues } from "../../../../models/workout";
import { normalizeTemplateWorkoutType } from "../utils/workoutSummary";

/**
 * useWorkoutsPage
 * Hook orquestrador para a página de Treinos (Workouts).
 * Segue o padrão de useMembersPage: concentra toda a lógica de negócio,
 * deixando o index.js como componente de renderização puro.
 */
export const useWorkoutsPage = () => {
    // ── Hooks de dados ──
    const templates = useWorkoutTemplates();
    const prescriptions = usePrescriptions();
    const { hasFeature } = usePlan();
    const canUseAi = hasFeature(FEATURE_KEYS.AI_WORKOUT_GENERATION);

    const aiWorkout = useAiWorkout();

    // ── Estado da UI ──
    const [activeTab, setActiveTab] = useState("templates"); // "templates" | "prescriptions"
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedPrescription, setSelectedPrescription] = useState(null);
    const [prescribingTemplate, setPrescribingTemplate] = useState(null);
    const [editingPrescription, setEditingPrescription] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [filterMember, setFilterMember] = useState(null);

    // Atualiza a lista quando o filtro muda
    useEffect(() => {
        if (activeTab === "prescriptions") {
            prescriptions.reload(filterMember?.id || null);
        }
    }, [filterMember?.id, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers de Navegação ──
    const handleSwitchToTemplates = useCallback(() => {
        setActiveTab("templates");
        setSelectedPrescription(null);
        setEditingPrescription(null);
    }, []);

    const handleSwitchToPrescriptions = useCallback(() => {
        setActiveTab("prescriptions");
        setSelectedTemplate(null);
        setPrescribingTemplate(null);
        setEditingPrescription(null);
    }, []);

    // ── Handlers de Templates ──
    const handleSelectTemplate = useCallback(async (template) => {
        if (selectedTemplate?.id === template.id) return;
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedTemplate(template);
        setPrescribingTemplate(null);
        setIsDirty(false);
    }, [selectedTemplate, isDirty]);

    const handleCreateNewTemplate = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças e criar um novo modelo?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedTemplate({ ...initialTemplateValues });
        setPrescribingTemplate(null);
        setIsDirty(false);
    }, [isDirty]);

    const handleSaveTemplate = useCallback(async (formData) => {
        const saved = await templates.handleSave(formData);
        if (saved) {
            setSelectedTemplate(saved);
            setIsDirty(false);
        }
    }, [templates]);

    const handleDuplicateTemplate = useCallback(async (template) => {
        const clone = {
            ...template,
            id: undefined,
            title: `${template.title || "Modelo"} (cópia)`,
            createdAt: undefined,
            updatedAt: undefined,
            createdBy: undefined,
            updatedBy: undefined,
        };
        const saved = await templates.handleSave(clone);
        if (saved) {
            setActiveTab("templates");
            setSelectedTemplate(saved);
            setPrescribingTemplate(null);
            setIsDirty(false);
        }
    }, [templates]);

    const handleDeleteTemplate = useCallback(async (id, title) => {
        const confirmed = await ConfirmDialog({
            title: "Excluir Modelo",
            text: `Deseja excluir o modelo "${title || "Sem título"}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });
        if (!confirmed) return;

        const success = await templates.handleDelete(id, title);
        if (success) {
            setSelectedTemplate(null);
            setIsDirty(false);
        }
    }, [templates]);

    // ── Handlers de Prescrição ──
    const handleStartPrescription = useCallback((template) => {
        setPrescribingTemplate({
            ...template,
            sessions: template.sessions || [],
        });
    }, []);

    const handleDirectPrescription = useCallback(() => {
        if (!filterMember) return;
        setActiveTab("prescriptions");
        setSelectedPrescription(null);
        setEditingPrescription(null);
        setPrescribingTemplate({
            title: `Treino - ${filterMember.name.split(' ')[0]}`,
            workoutType: "strength",
            weeklyFrequency: 3,
            totalWeeks: 4,
            sessions: [],
            preselectedMembers: [filterMember]
        });
    }, [filterMember]);

    const handlePrescribe = useCallback(async (prescriptionBase, membersList, saveAsTemplate = false) => {
        const results = await prescriptions.handlePrescribe(prescriptionBase, membersList);
        if (results) {
            if (saveAsTemplate) {
                await templates.handleSave({
                    title: prescriptionBase.templateTitle || "Modelo Salvo de Prescrição",
                    description: "",
                    workoutType: prescriptionBase.workoutType || "strength",
                    objective: prescriptionBase.objective || "",
                    level: prescriptionBase.level || "",
                    weeklyFrequency: prescriptionBase.weeklyFrequency || 1,
                    totalWeeks: prescriptionBase.totalWeeks || 1,
                    sessions: prescriptionBase.sessions || [],
                    isSingleDay: prescriptionBase.isSingleDay || false
                });
            }
            setPrescribingTemplate(null);
            setActiveTab("prescriptions");
        }
    }, [prescriptions, templates]);

    const handleEditPrescription = useCallback((prescription) => {
        setEditingPrescription({
            ...prescription,
            title: prescription.templateTitle,
            preselectedMembers: [{
                id: prescription.idMember,
                name: prescription.memberName,
                photo: prescription.memberPhoto
            }]
        });
    }, []);

    const handleUpdatePrescription = useCallback(async (prescriptionBase) => {
        const updateData = {
            templateTitle: prescriptionBase.templateTitle,
            workoutType: prescriptionBase.workoutType,
            sessions: prescriptionBase.sessions,
            startDate: prescriptionBase.startDate,
            endDate: prescriptionBase.endDate,
            weeklyFrequency: prescriptionBase.weeklyFrequency,
            isSingleDay: prescriptionBase.isSingleDay,
            totalWeeks: prescriptionBase.totalWeeks,
        };
        const result = await prescriptions.handleUpdate(
            editingPrescription.id,
            updateData,
            editingPrescription.idMember || filterMember?.id
        );
        if (result) {
            setEditingPrescription(null);
            setSelectedPrescription(result);
        }
    }, [prescriptions, editingPrescription, filterMember]);

    const handleSelectPrescription = useCallback((presc) => {
        setSelectedPrescription(presc);
        setPrescribingTemplate(null);
        handleEditPrescription(presc);
    }, [handleEditPrescription]);

    const handleDeletePrescription = useCallback(async (id, title, idMember) => {
        const confirmed = await ConfirmDialog({
            title: "Excluir Prescrição",
            text: `Deseja excluir esta prescrição? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });
        if (!confirmed) return;

        const memberId = idMember || editingPrescription?.idMember || filterMember?.id;
        const success = await prescriptions.handleDelete(id, title, memberId);
        if (success) {
            setSelectedPrescription(null);
            setEditingPrescription(null);
        }
    }, [prescriptions, editingPrescription, filterMember]);

    // ── IA ──
    const onAiGenerate = useCallback(async () => {
        const workout = await aiWorkout.handleGenerate();
        if (workout) {
            const generatedWorkoutType = aiWorkout.formData.workoutType || "swimming";

            // Injeta o tipo de treino em cada bloco e série gerados
            const blocksWithTypes = (workout.blocks || []).map((b, bIdx) => ({
                ...b,
                id: Date.now().toString() + "_b_" + bIdx,
                workoutType: generatedWorkoutType,
                method: "normal",
                sets: (b.sets || []).map((s, sIdx) => ({
                    ...s,
                    id: Date.now().toString() + "_s_" + bIdx + "_" + sIdx,
                    type: generatedWorkoutType
                }))
            }));

            let newWorkouts = [];
            let newBlocks = [];

            if (generatedWorkoutType === "strength" || generatedWorkoutType === "running") {
                newWorkouts = blocksWithTypes.map((b, idx) => ({
                    id: Date.now().toString() + "_w_" + idx,
                    name: b.name || `Treino ${String.fromCharCode(65 + idx)}`,
                    blocks: [b]
                }));
            } else {
                newBlocks = blocksWithTypes;
            }

            if (activeTab === "prescriptions" && prescribingTemplate) {
                setPrescribingTemplate(prev => ({
                    ...prev,
                    title: workout.title,
                    description: workout.description || "",
                    sessions: newWorkouts.length > 0 ? newWorkouts : prev.sessions,
                    weeklyFrequency: newWorkouts.length > 0 ? newWorkouts.length : prev.weeklyFrequency,
                    workoutType: generatedWorkoutType
                }));
            } else {
                setSelectedTemplate(normalizeTemplateWorkoutType({
                    ...initialTemplateValues,
                    title: workout.title,
                    description: workout.description || "",
                    sessions: newWorkouts.length > 0 ? newWorkouts : [{
                        id: Date.now().toString() + "_session",
                        name: "Sessão A",
                        sessionType: generatedWorkoutType,
                        blocks: newBlocks,
                    }],
                    weeklyFrequency: newWorkouts.length > 0 ? newWorkouts.length : initialTemplateValues.weeklyFrequency,
                    workoutType: generatedWorkoutType
                }, generatedWorkoutType));
                setPrescribingTemplate(null);
                setIsDirty(true);
            }
            aiWorkout.toggleModal();
        }
    }, [aiWorkout, activeTab, prescribingTemplate]);

    return {
        // Dados
        templates,
        prescriptions,
        canUseAi,
        aiWorkout,

        // UI State
        activeTab,
        selectedTemplate,
        selectedPrescription,
        prescribingTemplate,
        editingPrescription,
        filterMember,

        // Navigation
        handleSwitchToTemplates,
        handleSwitchToPrescriptions,

        // Template Actions
        handleSelectTemplate,
        handleCreateNewTemplate,
        handleSaveTemplate,
        handleDeleteTemplate,
        handleDuplicateTemplate,
        handleStartPrescription,

        // Prescription Actions
        handleDirectPrescription,
        handlePrescribe,
        handleSelectPrescription,
        handleEditPrescription,
        handleUpdatePrescription,
        handleDeletePrescription,
        setFilterMember,
        setEditingPrescription,
        setSelectedPrescription,
        setPrescribingTemplate,

        // IA
        onAiGenerate,

        // Loading
        isLoading: templates.loading && prescriptions.loading,
    };
};
